import React, { createContext, useContext, useEffect, useState } from 'react';

import type { CmxDocumentContentSlotData } from './cmxDocumentContentSlot';

/**
 * Local, non-redux UI state shared between `NoteNav` and `NoteViewer`: which note is
 * currently "active" (selected in the nav, shown in the viewer) and which note's source-document
 * modal (if any) is open. Deliberately plain `useState` -- no redux, no fetch -- both note KEYS
 * (`String(note.document_id)`) are derived from data already hydrated into `state.values['doc.notes']`
 * by the stateMapper; this context only tracks WHICH one is selected/open, never the note data
 * itself.
 *
 * `sourceModalNoteKey` is a SEPARATE field from `activeNoteKey` (not one field with a "modal is the
 * active note" convention) so closing the modal can restore prior nav/viewer state exactly (AC11):
 * `closeSourceModal` clears ONLY `sourceModalNoteKey`, leaving `activeNoteKey` (and therefore which
 * note the nav/viewer show) completely untouched.
 *
 * ## Which document opens first
 *
 * 
 * * BEGIN MATT COMMENT
 * Matt - Below is a bunch of claude garbage, what its doing is prioritizing standard doc over legacy doc, though standard doc for some reason is not rendering, you need to click the tab, we should not need a whole comment and new code to have this work properly
 * on my todo to actually look at this code (i have just tested the functionality), 
 * * END MATT COMMENT
 * 
 * 
 * `activeNoteKey` encodes a source hierarchy, because two unrelated document systems feed the
 * same pane:
 *
 *  1. a CMX Document Content, when the case supplies at least one via the `cmxDocumentContents` prop
 *     (`cmxDocumentContentFromCaseRun`, passed at `coding-screen.tsx`) -- the FIRST entry wins;
 *  2. otherwise the legacy documents, which `getDocumentsAsHtml` has already fetched
 *     (`/documents/v2` by `businessKey`, then `/documents/download/{id}`) into
 *     `state.values['doc.notes']`.
 *
 * `undefined` IS the legacy branch -- it is what `NoteViewer` falls through to -- so the second
 * case is the pre-existing behaviour, unchanged. Only the first case is new, and it exists because
 * the CMX Document Content is otherwise reachable only by clicking the strip entry, which makes a
 * URL that names a document open a pane that is not showing it.
 *
 * The list arrives as a prop rather than off `doc.notes` because the provider sits ABOVE the slots
 * and has no bound state value; `coding-screen.tsx` has the `caseRun` and passes it down.
 *
 * A `useState` initializer alone is not enough to pick this up, and that is not hypothetical: on
 * `CodingScreen`'s FIRST render `caseRun` is undefined (`getDetail` dispatches in a `useEffect` and
 * resolves later), so `cmxDocumentContents` arrives as `[]` on mount and only becomes real once the
 * case loads -- after the initializer has already run and been discarded. The effect below catches
 * that later arrival. It only acts while `activeNoteKey` is still `undefined` (nothing -- neither a
 * case-supplied document nor a coder click -- has claimed the pane yet), so it can't clobber a
 * manual selection made in between.
 */
export interface NoteInteractionValue {
  readonly activeNoteKey: string | undefined;
  readonly setActiveNoteKey: (key: string) => void;
  readonly sourceModalNoteKey: string | undefined;
  readonly openSourceModal: (key: string) => void;
  readonly closeSourceModal: () => void;
}

const NoteInteractionContext = createContext<NoteInteractionValue | null>(null);

export type NoteInteractionProviderProps = React.PropsWithChildren<{
  readonly cmxDocumentContents?: CmxDocumentContentSlotData;
}>;

export const NoteInteractionProvider: React.FC<
  NoteInteractionProviderProps
> = ({ children, cmxDocumentContents = [] }) => {
  const [activeNoteKey, setActiveNoteKey] = useState<string | undefined>(() =>
    cmxDocumentContents.length > 0 ? cmxDocumentContents[0].documentId : undefined
  );
  const [sourceModalNoteKey, setSourceModalNoteKey] = useState<
    string | undefined
  >(undefined);

  // Catches the case-supplied list arriving AFTER mount (see the doc comment above) -- the
  // initializer above only ever sees the value `cmxDocumentContents` had on first render.
  useEffect(() => {
    if (activeNoteKey === undefined && cmxDocumentContents.length > 0) {
      setActiveNoteKey(cmxDocumentContents[0].documentId);
    }
  }, [cmxDocumentContents, activeNoteKey]);

  const value: NoteInteractionValue = {
    activeNoteKey,
    setActiveNoteKey,
    sourceModalNoteKey,
    openSourceModal: setSourceModalNoteKey,
    closeSourceModal: () => setSourceModalNoteKey(undefined)
  };

  return (
    <NoteInteractionContext.Provider value={value}>
      {children}
    </NoteInteractionContext.Provider>
  );
};

/** Throws a clear, actionable error at the exact call site instead of silently working with
 *  `null` when a consumer renders outside `NoteInteractionProvider` -- mirrors
 *  `project_starburst/interface/context.tsx`'s `useCodingScreen` precedent. */
export function useNoteInteraction(): NoteInteractionValue {
  const ctx = useContext(NoteInteractionContext);
  if (!ctx) {
    throw new Error(
      'useNoteInteraction must be used within NoteInteractionProvider'
    );
  }
  return ctx;
}
